(() => {
  const instances = new Map();
  const animationDuration = 300;

  class AWFaq {
    constructor(root) {
      this.root = root;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.root.addEventListener("click", this.onClick);
      this.root.addEventListener("keydown", this.onKeydown);
    }

    shouldAnimate() {
      return !this.root.classList.contains("aw-faq--no-animation")
        && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    stopAnimation(answer) {
      if (!answer) return;

      if (answer.__awFaqTransitionEnd) {
        answer.removeEventListener("transitionend", answer.__awFaqTransitionEnd);
        answer.__awFaqTransitionEnd = null;
      }

      answer.style.transition = "none";

      if (!answer.hidden) {
        answer.style.height = `${answer.offsetHeight}px`;
      }

      // Force the browser to apply the height before the next animation starts.
      void answer.offsetHeight;
    }

    resetAnswerStyles(answer) {
      if (!answer) return;

      answer.style.height = "";
      answer.style.overflow = "";
      answer.style.transition = "";
    }

    onClick(event) {
      const trigger = event.target.closest("[data-aw-faq-trigger]");
      if (!trigger || !this.root.contains(trigger)) return;

      const item = trigger.closest("[data-aw-faq-item]");
      if (!item) return;

      this.toggleItem(item);
    }

    onKeydown(event) {
      if (event.key !== "Escape") return;

      const item = event.target.closest("[data-aw-faq-item]");
      if (!item || !this.root.contains(item)) return;

      this.closeItem(item);
      const trigger = item.querySelector("[data-aw-faq-trigger]");
      if (trigger) {
        trigger.focus();
      }
    }

    toggleItem(item) {
      if (item.classList.contains("is-open")) {
        this.closeItem(item);
        return;
      }

      this.openItem(item);
    }

    openItem(item) {
      const trigger = item.querySelector("[data-aw-faq-trigger]");
      const answer = item.querySelector("[data-aw-faq-answer]");

      item.classList.add("is-open");

      if (trigger) {
        trigger.setAttribute("aria-expanded", "true");
      }

      if (answer) {
        this.stopAnimation(answer);
        answer.hidden = false;

        if (!this.shouldAnimate()) {
          this.resetAnswerStyles(answer);
          return;
        }

        answer.style.overflow = "hidden";
        answer.style.height = "0px";
        answer.style.transition = `height ${animationDuration}ms ease`;

        requestAnimationFrame(() => {
          answer.style.height = `${answer.scrollHeight}px`;
        });

        const onTransitionEnd = (event) => {
          if (event.target !== answer || event.propertyName !== "height") return;

          answer.removeEventListener("transitionend", onTransitionEnd);
          answer.__awFaqTransitionEnd = null;
          this.resetAnswerStyles(answer);
        };

        answer.__awFaqTransitionEnd = onTransitionEnd;
        answer.addEventListener("transitionend", onTransitionEnd);
      }
    }

    closeItem(item) {
      const trigger = item.querySelector("[data-aw-faq-trigger]");
      const answer = item.querySelector("[data-aw-faq-answer]");

      item.classList.remove("is-open");

      if (trigger) {
        trigger.setAttribute("aria-expanded", "false");
      }

      if (answer) {
        this.stopAnimation(answer);

        if (!this.shouldAnimate()) {
          answer.hidden = true;
          this.resetAnswerStyles(answer);
          return;
        }

        answer.hidden = false;
        answer.style.overflow = "hidden";
        answer.style.height = `${answer.scrollHeight}px`;
        answer.style.transition = `height ${animationDuration}ms ease`;

        requestAnimationFrame(() => {
          answer.style.height = "0px";
        });

        const onTransitionEnd = (event) => {
          if (event.target !== answer || event.propertyName !== "height") return;

          answer.removeEventListener("transitionend", onTransitionEnd);
          answer.__awFaqTransitionEnd = null;
          answer.hidden = true;
          this.resetAnswerStyles(answer);
        };

        answer.__awFaqTransitionEnd = onTransitionEnd;
        answer.addEventListener("transitionend", onTransitionEnd);
      }
    }

    openBlock(blockId) {
      const item = this.root.querySelector(`[data-block-id="${blockId}"]`);
      if (!item) return;

      this.openItem(item);
    }

    destroy() {
      this.root.removeEventListener("click", this.onClick);
      this.root.removeEventListener("keydown", this.onKeydown);
    }
  }

  const init = (container = document) => {
    container.querySelectorAll("[data-aw-faq]").forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new AWFaq(root));
    });
  };

  const destroy = (container) => {
    container.querySelectorAll("[data-aw-faq]").forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    init();
  });

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest("[data-aw-faq]");
    if (!section) return;

    const instance = instances.get(section);
    if (!instance) return;

    instance.openBlock(event.detail.blockId);
  });
})();
